import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Form, Table } from "antd";
import { FormListProps } from "antd/es/form";
import { ColumnsType } from "antd/lib/table";
import * as React from "react";
import { ZAttrMeta } from "src/business/attribute";
import { typeIcons } from "src/business/attribute/typeIcons";
import { classNames } from "src/common/classNames";

import { AttrFeatures } from "./AttrFeatures";
import { AttrMetaEditorModal } from "./AttrMetaEditorModal";
import * as styles from "./AttrsListEditor.module.less";

/* eslint jsx-a11y/label-has-associated-control: "off" */

type PropsAttrsListEditor = {
  name: FormListProps["name"];
  attrFeatures?: AttrFeatures;
};

const newAttr = (): ZAttrMeta => ({
  name: "",
  title: "",
  type: { name: "string" },
  uid: "",
  displayId: 0,
});

export const AttrsListEditor: React.FC<PropsAttrsListEditor> = (props) => {
  const { name, attrFeatures } = props;
  const form = Form.useFormInstance();
  const list: ZAttrMeta[] = Form.useWatch(name, form) || [];
  const [curAttr, setCurAttr] = React.useState<ZAttrMeta | undefined>();
  const [curAttrIndex, setCurAttrIndex] = React.useState(0);
  const columns = (remove: (index: number) => void): ColumnsType<ZAttrMeta> => [
    {
      key: "displayId",
      title: "Id",
      render: (_, row) => row.displayId,
    },
    {
      key: "name",
      title: "Название",
      render: (_, row) => (
        <Button
          type="link"
          onClick={() => {
            setCurAttr(row);
            setCurAttrIndex(list.indexOf(row));
          }}
        >
          {row.name}
        </Button>
      ),
    },
    {
      key: "title",
      title: "Описание",
      render: (_, row) => row.title,
    },
    {
      key: "type",
      title: "Тип",
      render: (_, row) => (
        <span>
          {typeIcons[row.type.name]} {row.type.name}
        </span>
      ),
    },
    {
      key: "uuid",
      title: "UUID",
      render: (_, row) => <span style={{ fontSize: "90%" }}>{row.uid}</span>,
    },
    {
      key: "@remove",
      title: " ",
      render: (_, row) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => remove(list.indexOf(row))}
        />
      ),
    },
  ];
  // Проверка удалена по требованию MDM2-397.
  const isRequired = false;
  const rules: FormListProps["rules"] = [];
  if (isRequired) {
    rules.push({
      validator: (_rule, value: ZAttrMeta[]) => {
        if (value.length === 0)
          return Promise.reject(
            Error("Необходимо добавить хотя бы один атрибут"),
          );
        return Promise.resolve();
      },
    });
  }
  rules.push({
    validator: (_rule, value: ZAttrMeta[]) => {
      const dup = getDupName(value);
      if (dup) {
        return Promise.reject(Error(`Дублируется имя атрибута "${dup}"`));
      }
      return Promise.resolve();
    },
  });
  rules.push({
    validator: (_rule, value: ZAttrMeta[]) => {
      const bad = value.find((it) => !it.name);
      if (bad) {
        return Promise.reject(
          Error(`Недопустимо наличие атрибутов без названия`),
        );
      }
      return Promise.resolve();
    },
  });
  return (
    <Form.List name={name} rules={rules}>
      {(fields, { add, remove }, { errors }) => (
        <div className={styles.box}>
          <div
            className={classNames([
              "ant-col ant-form-item-label",
              styles.header,
            ])}
          >
            <label
              className={classNames([
                [isRequired, "ant-form-item-required"],
                styles.title,
              ])}
            >
              Атрибуты
            </label>
            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => {
                const index = fields.length;
                add(newAttr(), index);
                setCurAttr((form.getFieldValue(name) ?? [])[index]);
                setCurAttrIndex(index);
              }}
            >
              Добавить атрибут
            </Button>
          </div>
          <div style={{ color: "#e9033a" }}>
            <Form.ErrorList errors={errors} />
          </div>
          <Table
            columns={columns(remove)}
            dataSource={list}
            pagination={false}
            size="small"
          />
          <AttrMetaEditorModal
            value={curAttr}
            close={() => setCurAttr(undefined)}
            onChange={(v) => {
              const newList = [...list];
              if (curAttrIndex >= 0 && curAttrIndex < newList.length) {
                newList[curAttrIndex] = v;
                form.setFieldValue(name, newList);
              }
            }}
            attrFeatures={attrFeatures}
          />
        </div>
      )}
    </Form.List>
  );
};

const getDupName = (list: ZAttrMeta[]): string | undefined => {
  const used = new Set<string>();
  return list.find(({ name }) => {
    if (used.has(name)) return true;
    used.add(name);
    return false;
  })?.name;
};
